import * as d3 from 'd3';
import { exportSvgAsPng, ISharedD3ChartProps } from '../../vis-shared';

import { Size, Margin } from '../../visTypes';

export interface INode {
  id: string;
  label: string;
  parentId?: string;
  count?: number;
  stroke: string;
  fill: string;
}

export interface ITreeBranchConfig extends ISharedD3ChartProps {
  target: any;
  nodes: INode[];
  height?: number;
  width?: number;
  showValues?: boolean;
}

export class TreeBranch {
  target: any;
  size: Size;
  chartSize: Size;
  margin: Margin;
  svg: d3.Selection<any, any, any, any>;
  chartSelection: d3.Selection<SVGGElement, any, any, any>;
  tooltip: d3.Selection<HTMLDivElement, any, any, any>;
  nodes: INode[];
  tree: d3.HierarchyNode<INode>;
  exportFileName: string;
  exportTitle: string | null;

  constructor(config: ITreeBranchConfig) {
    this.target = config.target;
    this.exportFileName = config.exportFileName || 'SankeyDiagram';
    this.exportTitle = config.exportTitle || null;
    this.nodes = config.nodes;
    const domWidth = Math.floor(parseInt(d3.select(this.target).style('width'), 10));
    const width = config.width || domWidth;
    const height = config.height || 20;
    this.size = {
      width,
      height,
    };
    // const rightMargin = 0; // this.calculateNeededMargin(config.nodes.map((d) => d.label));
    this.margin = {
      top: 5,
      bottom: 5,
      left: 0,
      right: 0,
    };
    this.chartSize = {
      width: width - this.margin.left - this.margin.right,
      height: height - this.margin.top - this.margin.bottom,
    };

    this.svg = d3
      .select(this.target)
      .style('position', 'relative')
      .append('svg')
      .classed('tree-branch', true)
      .attr('height', this.size.height)
      .attr('width', this.size.width);

    this.chartSelection = this.svg.append('g').attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);

    this.tooltip = d3
      .select('body')
      .append('div')
      .style('visibility', 'hidden')
      .style('width', '110px')
      .style('font-size', '0.8em')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('z-index', 150);

    const stratifier = d3
      .stratify<INode>()
      .id((d) => d.id)
      .parentId((d) => d.parentId);

    this.tree = stratifier(this.nodes);
    this.tree.sum((d) => d.count || 0);

    this.draw();
  }

  draw(): void {
    const partitioner = d3
      .partition<INode>()
      .size([this.chartSize.height, this.chartSize.width])
      .padding(0)
      .round(false);

    const treeWithLayout = partitioner(this.tree);

    const cells = this.chartSelection
      .selectAll('g.cell')
      .data(treeWithLayout.descendants())
      .join('g')
      .attr('class', 'cell')
      .attr('transform', (d) => `translate(${d.y0},${d.x0})`);

    cells
      .append('rect')
      .attr('width', (d) => d.y1 - d.y0)
      .attr('height', (d) => d.x1 - d.x0)
      .attr('fill', (d) => d.data.fill)
      .attr('stroke', (d) => d.data.stroke)
      .attr('stroke-width', '4');

    const cellsWithBigText = cells
      .filter((d) => d.depth < 2 && d.x1 - d.x0 > 30)
      .append('g')
      .attr('class', 'text-group-large')
      .attr('transform', 'translate(5, 10)');
    const cellsWithSmallText = cells
      .filter((d) => d.depth >= 2 && d.x1 - d.x0 > 10)
      .append('g')
      .attr('class', 'text-group-small')
      .attr('transform', (d) => {
        const yOffset = Math.min(9, (d.x1 - d.x0) / 2);
        return `translate(5, ${yOffset})`;
      });

    cellsWithBigText
      .append('text')
      .attr('fill', (d) => d.data.stroke)
      .style('font-weight', 700)
      .attr('font-size', 14)
      .attr('x', 0)
      .attr('y', 0)
      .attr('dy', '0.35em')
      .text((d) => d.value || 0);

    cellsWithBigText
      .append('text')
      .attr('fill', (d) => d.data.stroke)
      .style('font-weight', 700)
      .attr('font-size', 12)
      .attr('x', 0)
      .attr('y', 12)
      .attr('dy', '0.35em')
      .text((d) => d.data.label);

    cellsWithSmallText
      .append('text')
      .attr('fill', (d) => d.data.stroke)
      .style('font-weight', 700)
      .attr('font-size', 11)
      .attr('x', 0)
      .attr('y', 0)
      .attr('dy', '0.35em')
      .text((d) => d.value || 0);

    cellsWithSmallText
      .append('text')
      .attr('fill', (d) => d.data.stroke)
      .style('font-weight', 700)
      .attr('font-size', 12)
      .attr('x', 20)
      .attr('y', 0)
      .attr('dy', '0.35em')
      .text((d) => d.data.label);
  }

  calculateNeededMargin(labels: string[]): number {
    const svg = d3.select(this.target).append('svg');
    // Calculate the width needed for the labels on the left side
    const widthCalcContainer = svg
      .append('text')
      .attr('font-family', 'Manrope')
      .style('font-weight', 700)
      .style('font-size', '12px')
      .style('text-transform', 'uppercase');
    const widthCalc = widthCalcContainer.append('tspan');
    let maxTextLength = 0;
    labels.forEach((d) => {
      widthCalc.text(d);
      // @ts-ignore
      const w = widthCalc.node().getComputedTextLength();
      maxTextLength = Math.max(maxTextLength, w);
    });
    svg.remove();
    return maxTextLength;
  }

  exportToPng(): void {
    const customCss = 'text { font-family: Inter }';
    exportSvgAsPng(this.svg.node(), {
      customCss,
      scale: 3,
      backgroundColor: 'white',
      fileName: this.exportFileName,
      title: this.exportTitle || undefined,
    });
  }
}
